import type { CompletedToolActivity } from "../shared/mcp-chat-types";
import type { ToolActivity } from "./mcp-tool-activity";

type ToolActivityLike = Pick<ToolActivity, "requestId" | "toolCallId" | "status"> & Partial<ToolActivity>;

export function getToolActivityIdentity(activity: Pick<ToolActivityLike, "requestId" | "toolCallId">): string {
  return activity.toolCallId || activity.requestId;
}

export function mergeToolActivityRecord<T extends ToolActivityLike>(existing: T | undefined, incoming: T): T {
  if (!existing) {
    return { ...incoming };
  }

  const keepTerminalStatus = incoming.status === "running" && existing.status !== "running";
  const status = keepTerminalStatus ? existing.status : incoming.status;

  return {
    ...existing,
    ...incoming,
    status,
    result: incoming.result ?? existing.result,
    error: incoming.error ?? existing.error,
    startTime: existing.startTime ?? incoming.startTime,
    duration: incoming.duration ?? existing.duration
  };
}

export function mergeToolActivityCollections<T extends ToolActivityLike>(...collections: T[][]): T[] {
  const order: string[] = [];
  const merged = new Map<string, T>();

  for (const collection of collections) {
    for (const activity of collection) {
      const key = getToolActivityIdentity(activity);
      if (!merged.has(key)) {
        order.push(key);
      }
      merged.set(key, mergeToolActivityRecord(merged.get(key), activity));
    }
  }

  return order.map((key) => merged.get(key)!);
}

export function mergeCompletedToolActivities(
  existing?: CompletedToolActivity[],
  incoming?: CompletedToolActivity[]
): CompletedToolActivity[] | undefined {
  const merged = mergeToolActivityCollections(existing || [], incoming || []).filter(
    (activity): activity is CompletedToolActivity => activity.status !== "blocked"
  );
  return merged.length > 0 ? merged : undefined;
}

/**
 * Shrink a message's tool transcript to what the UI actually reads before it is
 * written to chat history.
 *
 * The raw `result` of every tool call was being stored verbatim and never read
 * again: the transcript's result line comes from the activity's `status`
 * (`getToolActivityStatusText`), its search text from label/description/status,
 * and its grouping from `toolName` + `input.operation`. Measured on a 99 MB
 * chat-history.db: 0.45 MB of actual message text against 66.5 MB of
 * transcripts, of which `result` alone was 92.8% and the rest of `input` a
 * further 4.6% — roughly 8.6 MB/day for a single developer. Dropping both
 * leaves every rendered row and every search hit identical at ~1/38th the size.
 *
 * Live in-memory activities keep their full `result`; only the persisted copy
 * is trimmed. Telemetry is untouched and remains the durable home for raw tool
 * results.
 */
export function toPersistedToolActivities(activities: CompletedToolActivity[]): CompletedToolActivity[] {
  return activities.map((activity: CompletedToolActivity): CompletedToolActivity => {
    const operation: unknown = activity.input?.operation;
    const trimmed: CompletedToolActivity = {
      requestId: activity.requestId,
      toolCallId: activity.toolCallId,
      toolName: activity.toolName,
      status: activity.status,
      isMutating: activity.isMutating,
      description: activity.description,
      startTime: activity.startTime,
      duration: activity.duration,
      specialistAgent: activity.specialistAgent,
      specialistAccumulated: activity.specialistAccumulated
    };
    // `operation` is the only member of `input` any consumer reads — it drives
    // the transcript group key and the group label.
    if (typeof operation === "string") trimmed.input = { operation };
    return trimmed;
  });
}

export function buildCompletedToolActivitiesFromActivities(
  activities: ToolActivity[]
): CompletedToolActivity[] | undefined {
  const completedToolActivities = mergeToolActivityCollections(activities)
    .filter((activity) => activity.status !== "running" && activity.status !== "blocked")
    .map((activity) => ({
      requestId: activity.requestId,
      toolCallId: activity.toolCallId,
      toolName: activity.toolName,
      status: activity.status as "completed" | "error",
      isMutating: activity.isMutating,
      description: activity.description,
      input: activity.input,
      result: activity.result,
      error: activity.error,
      startTime: activity.startTime,
      duration: activity.duration,
      specialistAgent: activity.specialistAgent,
      specialistAccumulated: activity.specialistAccumulated
    }));

  return completedToolActivities.length > 0 ? completedToolActivities : undefined;
}
